import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'

export interface TokenAccess {
  canViewToken: boolean
  canRegenerateToken: boolean
  canDeleteToken: boolean
}

/**
 * Who may see and manage Plivo auth tokens, from the `api_credentials` RBAC module.
 * Every customer profile carries these grants; only Plivo support sessions do not, so
 * for customers this reads true everywhere.
 *
 * The backend is the real gate (it omits the token and sends `tokenVisible: false`) —
 * this hook exists so no screen has to spell the module/permission pair itself, which is
 * how a gate drifts. Screens that also run inside the flow-builder embed must NOT use it:
 * with no PermissionsProvider above them `usePermissions` returns a deny-all context, so
 * they key off whether the token is present in the response instead.
 */
export const useTokenAccess = (): TokenAccess => {
  const { hasPermission } = usePermissions()

  return {
    canViewToken: hasPermission(RbacModules.apiCredentials, RbacPermissionTypes.view),
    canRegenerateToken: hasPermission(RbacModules.apiCredentials, RbacPermissionTypes.edit),
    canDeleteToken: hasPermission(RbacModules.apiCredentials, RbacPermissionTypes.delete),
  }
}
